import React from 'react';
import styled, { keyframes } from 'styled-components';
import OutfallDetailPanel from './OutfallDetailPanel';
import ReceivingWaterStatusCard from './ReceivingWaterStatusCard';
import ReceivingWaterDetailPanel from './ReceivingWaterDetailPanel';
import { ApiState, Mode, useFacilityModeData } from './FacilityModeDataContext';

// ─── utilities ────────────────────────────────────────────────────────────────

function minutesAgo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
}

// ─── echo card helper ─────────────────────────────────────────────────────────

type EchoCardResult =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | {
      state: 'ok';
      value: string;
      labelLine: string;
      sub: string;
      freshness?: { label: string; tone: string };
    };

function echoCardProps(
  echo: ApiState,
  sourceLabel: string,
  programPrefix: 'CAA' | 'CWA' | 'RCRA'
): EchoCardResult {
  if (echo.state === 'loading') return { state: 'loading' };
  if (echo.state === 'error') return { state: 'error', error: echo.error };
  const { data } = echo;
  if (data?.available === false) {
    return {
      state: 'ok',
      value: 'No ECHO record',
      labelLine: 'Facility not found in ECHO for this location',
      sub: `${data.reason} · echo.epa.gov`,
      freshness: { label: 'n/a', tone: 'cached' },
    };
  }
  const f = data?.facility;
  if (!f) return { state: 'error', error: 'Unexpected ECHO payload' };
  const status: string =
    f[`${programPrefix}ComplianceStatus`] || f.FacComplianceStatus || 'Unknown';
  const inspKey =
    programPrefix === 'CAA'
      ? 'CAAEvaluationCount'
      : programPrefix === 'CWA'
        ? 'CWAInspectionCount'
        : 'RCRAInspectionCount';
  const insp: string = f[inspKey] ?? '0';
  const last = f.FacDateLastInspection
    ? `last insp ${f.FacDateLastInspection}`
    : 'no inspection on file';
  const formal: string | undefined = f[`${programPrefix}FormalActionCount`];
  const formalLine =
    formal && formal !== '0' ? ` · ${formal} formal action${formal === '1' ? '' : 's'}` : '';
  return {
    state: 'ok',
    value: status,
    labelLine: `${insp} inspections · ${last}${formalLine}`,
    sub: `${sourceLabel} · echo.epa.gov · last 36 months`,
  };
}

// ─── styled components ────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ModeToggleBar = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 4.5rem;
  pointer-events: auto;
`;

const ModeToggleGroup = styled.div`
  display: flex;
  gap: 3px;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 999px;
  padding: 3px;
  backdrop-filter: blur(4px);
`;

const ModePill = styled.button<{ $active: boolean; $color: string }>`
  border: none;
  border-radius: 999px;
  padding: 0.3rem 1rem;
  font-size: 0.78rem;
  font-weight: 600;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  background: ${({ $active, $color }) => ($active ? $color : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.6)')};
  &:hover {
    color: #fff;
    background: ${({ $active, $color }) => ($active ? $color : 'rgba(255,255,255,0.1)')};
  }
`;

const CardsArea = styled.div`
  pointer-events: auto;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(to top, rgba(10, 16, 32, 0.88) 0%, transparent 100%);
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const Card = styled.article<{ $unavailable?: boolean }>`
  flex: 0 0 210px;
  background: ${({ $unavailable }) =>
    $unavailable ? 'rgba(30, 32, 44, 0.88)' : 'rgba(13, 20, 38, 0.9)'};
  border: 1px solid
    ${({ $unavailable }) => ($unavailable ? 'rgba(255,255,255,0.07)' : 'rgba(52,211,153,0.18)')};
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  backdrop-filter: blur(8px);
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
  gap: 0.25rem;
`;

const SourcePill = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: #8896ac;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FreshnessPill = styled.span<{ $tone: string }>`
  flex-shrink: 0;
  font-size: 0.6rem;
  border-radius: 999px;
  padding: 1px 6px;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === 'live'
      ? 'rgba(52,211,153,0.15)'
      : $tone === 'weekly'
        ? 'rgba(251,191,36,0.15)'
        : 'rgba(100,116,139,0.18)'};
  color: ${({ $tone }) =>
    $tone === 'live' ? '#34d399' : $tone === 'weekly' ? '#fbbf24' : '#64748b'};
`;

const CardValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  margin-bottom: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardValueMuted = styled(CardValue)`
  font-size: 0.82rem;
  color: #64748b;
`;

const CardLabel = styled.div`
  font-size: 0.68rem;
  color: #8896ac;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  margin-bottom: 0.15rem;
  line-height: 1.35;
`;

const CardSub = styled.div`
  font-size: 0.6rem;
  color: rgba(136, 150, 172, 0.65);
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  line-height: 1.3;
`;

const PulseBar = styled.div`
  height: 18px;
  border-radius: 4px;
  margin-bottom: 0.3rem;
  background: linear-gradient(
    90deg,
    rgba(52, 211, 153, 0.08) 0%,
    rgba(52, 211, 153, 0.2) 50%,
    rgba(52, 211, 153, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

// ─── DataCard component ───────────────────────────────────────────────────────

interface IDataCardProps {
  source: string;
  freshness: { label: string; tone: string };
  state: 'loading' | 'error' | 'ok';
  value?: string;
  labelLine?: string;
  sub?: string;
  error?: string;
}

const DataCard: React.FC<IDataCardProps> = ({
  source,
  freshness,
  state,
  value,
  labelLine,
  sub,
  error,
}) => {
  if (state === 'loading') {
    return (
      <Card>
        <CardHead>
          <SourcePill>{source}</SourcePill>
          <FreshnessPill $tone={freshness.tone}>{freshness.label}</FreshnessPill>
        </CardHead>
        <PulseBar aria-label="Loading" />
        <CardSub>Loading…</CardSub>
      </Card>
    );
  }
  if (state === 'error') {
    return (
      <Card $unavailable>
        <CardHead>
          <SourcePill>{source}</SourcePill>
          <FreshnessPill $tone="cached">unavailable</FreshnessPill>
        </CardHead>
        <CardValueMuted>Data unavailable</CardValueMuted>
        <CardLabel>{error || 'Upstream API did not respond.'}</CardLabel>
        <CardSub>No fallback values shown. Retry later.</CardSub>
      </Card>
    );
  }
  return (
    <Card>
      <CardHead>
        <SourcePill>{source}</SourcePill>
        <FreshnessPill $tone={freshness.tone}>{freshness.label}</FreshnessPill>
      </CardHead>
      <CardValue>{value}</CardValue>
      <CardLabel>{labelLine}</CardLabel>
      <CardSub>{sub}</CardSub>
    </Card>
  );
};

// ─── ModeToggle ───────────────────────────────────────────────────────────────

const MODES: { id: Mode; label: string; icon: string; color: string }[] = [
  { id: 'air', label: 'Air', icon: '💨', color: '#E24B4A' },
  { id: 'water', label: 'Water', icon: '💧', color: '#3FC5D5' },
  { id: 'land', label: 'Land', icon: '⛰', color: '#EF9F27' },
];

// ─── card group components ────────────────────────────────────────────────────

const AirCards: React.FC<{ airnow: ApiState; nws: ApiState }> = ({ airnow, nws }) => {
  let airnowProps: IDataCardProps;
  if (airnow.state === 'loading') {
    airnowProps = {
      state: 'loading',
      source: 'EPA AirNow',
      freshness: { label: 'hourly', tone: 'live' },
    };
  } else if (airnow.state === 'error') {
    airnowProps = {
      state: 'error',
      source: 'EPA AirNow',
      freshness: { label: 'hourly', tone: 'live' },
      error: airnow.error,
    };
  } else {
    const obs: any[] = Array.isArray(airnow.data) ? airnow.data : [];
    const aqiObs = obs.find((o) => typeof o.AQI === 'number');
    if (!aqiObs) {
      airnowProps = {
        state: 'error',
        source: 'EPA AirNow',
        freshness: { label: 'hourly', tone: 'live' },
        error: 'No AQI observation in radius',
      };
    } else {
      airnowProps = {
        state: 'ok',
        source: 'EPA AirNow',
        freshness: { label: 'hourly', tone: 'live' },
        value: `AQI ${aqiObs.AQI}`,
        labelLine: `${aqiObs.Category?.Name || 'Unknown'} · ${aqiObs.ParameterName || ''} dominant · ${aqiObs.ReportingArea || aqiObs.SiteName || 'unknown'}`,
        sub: `Updated ${aqiObs.DateObserved || 'today'} ${aqiObs.HourObserved ?? ''}:00 ${aqiObs.LocalTimeZone || ''} · airnowapi.org`,
      };
    }
  }

  let nwsProps: IDataCardProps;
  if (nws.state === 'loading') {
    nwsProps = {
      state: 'loading',
      source: 'NOAA NWS',
      freshness: { label: 'live', tone: 'live' },
    };
  } else if (nws.state === 'error') {
    nwsProps = {
      state: 'error',
      source: 'NOAA NWS',
      freshness: { label: 'live', tone: 'live' },
      error: nws.error,
    };
  } else {
    const d = nws.data;
    if (!d || d.windSpeedMph == null) {
      nwsProps = {
        state: 'error',
        source: 'NOAA NWS',
        freshness: { label: 'live', tone: 'live' },
        error: 'No wind observation',
      };
    } else {
      nwsProps = {
        state: 'ok',
        source: 'NOAA NWS',
        freshness: { label: 'live', tone: 'live' },
        value: `${d.windSpeedMph} mph`,
        labelLine: `${d.windDirCompass || ''} · ${d.stationName || 'station'}`,
        sub: `Updated ${minutesAgo(d.observedAt) ?? '?'} min ago · api.weather.gov`,
      };
    }
  }

  return (
    <>
      <DataCard {...airnowProps} />
      <DataCard {...nwsProps} />
    </>
  );
};

const EchoAirCard: React.FC<{ echo: ApiState }> = ({ echo }) => {
  const p = echoCardProps(echo, 'EPA ECHO · Air', 'CAA');
  return (
    <DataCard
      source="EPA ECHO · Air"
      freshness={p.state === 'ok' && p.freshness ? p.freshness : { label: 'daily', tone: 'live' }}
      {...p}
    />
  );
};

const WaterCards: React.FC<{
  usgs: ApiState;
  echo: ApiState;
  nwps: ApiState;
}> = ({ usgs, echo, nwps }) => {
  let usgsProps: IDataCardProps;
  if (usgs.state === 'loading') {
    usgsProps = {
      state: 'loading',
      source: 'USGS Water',
      freshness: { label: 'live', tone: 'live' },
    };
  } else if (usgs.state === 'error') {
    usgsProps = {
      state: 'error',
      source: 'USGS Water',
      freshness: { label: 'live', tone: 'live' },
      error: usgs.error,
    };
  } else {
    const d = usgs.data;
    if (!d || d.stationId == null) {
      usgsProps = {
        state: 'error',
        source: 'USGS Water',
        freshness: { label: 'live', tone: 'live' },
        error: 'No nearby USGS gauge with current data',
      };
    } else {
      const parts: string[] = [];
      if (d.flowCfs != null) parts.push(`${d.flowCfs} cfs`);
      if (d.gageHeightFt != null) parts.push(`${d.gageHeightFt} ft`);
      usgsProps = {
        state: 'ok',
        source: 'USGS Water',
        freshness: { label: 'live', tone: 'live' },
        value: parts.join(' · ') || '—',
        labelLine: `${d.stationName} · ${d.distanceMiles ?? '?'} mi`,
        sub: `Updated ${minutesAgo(d.observedAt) ?? '?'} min ago · waterdata.usgs.gov`,
      };
    }
  }

  const echoWater = echoCardProps(echo, 'EPA ECHO · DMR', 'CWA');

  let nwpsProps: IDataCardProps;
  if (nwps.state === 'loading') {
    nwpsProps = {
      state: 'loading',
      source: 'NOAA NWPS',
      freshness: { label: 'hourly', tone: 'live' },
    };
  } else if (nwps.state === 'error') {
    nwpsProps = {
      state: 'error',
      source: 'NOAA NWPS',
      freshness: { label: 'hourly', tone: 'live' },
      error: nwps.error,
    };
  } else {
    const d = nwps.data;
    if (d?.available === false) {
      nwpsProps = {
        state: 'ok',
        source: 'NOAA NWPS',
        freshness: { label: 'n/a', tone: 'cached' },
        value: 'No nearby gauge',
        labelLine: 'NWPS gauge density is sparse in this area',
        sub: `${d.reason} · water.noaa.gov`,
      };
    } else if (!d?.lid) {
      nwpsProps = {
        state: 'error',
        source: 'NOAA NWPS',
        freshness: { label: 'hourly', tone: 'live' },
        error: 'NWPS returned unexpected payload',
      };
    } else {
      nwpsProps = {
        state: 'ok',
        source: 'NOAA NWPS',
        freshness: { label: 'hourly', tone: 'live' },
        value: d.currentCategory || 'No flood',
        labelLine: `${d.gaugeName} · forecast peak ${d.forecastPeak ?? '?'} ft (${d.forecastPeakCategory || '—'})`,
        sub: `peak ETA ${d.forecastPeakTime || '?'} · water.noaa.gov`,
      };
    }
  }

  return (
    <>
      <DataCard {...usgsProps} />
      <DataCard
        source="EPA ECHO · DMR"
        freshness={
          echoWater.state === 'ok' && echoWater.freshness
            ? echoWater.freshness
            : { label: 'daily', tone: 'live' }
        }
        {...echoWater}
      />
      <DataCard {...nwpsProps} />
    </>
  );
};

const LandCards: React.FC<{ echo: ApiState; eq: ApiState }> = ({ echo, eq }) => {
  const rcra = echoCardProps(echo, 'EPA RCRA · ECHO', 'RCRA');

  let triProps: IDataCardProps;
  if (echo.state === 'loading') {
    triProps = {
      state: 'loading',
      source: 'EPA TRI · via ECHO',
      freshness: { label: 'annual', tone: 'weekly' },
    };
  } else if (echo.state === 'error') {
    triProps = {
      state: 'error',
      source: 'EPA TRI · via ECHO',
      freshness: { label: 'annual', tone: 'weekly' },
      error: echo.error,
    };
  } else if (echo.data?.available === false) {
    triProps = {
      state: 'error',
      source: 'EPA TRI · via ECHO',
      freshness: { label: 'annual', tone: 'weekly' },
      error: echo.data.reason,
    };
  } else {
    const lbs = Number(echo.data?.facility?.TRIReleasesTransfers);
    if (!Number.isFinite(lbs) || lbs === 0) {
      triProps = {
        state: 'ok',
        source: 'EPA TRI · via ECHO',
        freshness: { label: 'annual', tone: 'cached' },
        value: 'No releases reported',
        labelLine: 'Facility not in TRI inventory or releases below threshold',
        sub: 'EPA ECHO · TRIReleasesTransfers field',
      };
    } else {
      triProps = {
        state: 'ok',
        source: 'EPA TRI · via ECHO',
        freshness: { label: 'annual', tone: 'weekly' },
        value: `${lbs.toLocaleString()} lb`,
        labelLine: 'TRI on-site releases + transfers (most recent reporting year)',
        sub: 'EPA ECHO · TRIReleasesTransfers field',
      };
    }
  }

  let eqProps: IDataCardProps;
  if (eq.state === 'loading') {
    eqProps = {
      state: 'loading',
      source: 'USGS Earthquake',
      freshness: { label: 'live', tone: 'live' },
    };
  } else if (eq.state === 'error') {
    eqProps = {
      state: 'error',
      source: 'USGS Earthquake',
      freshness: { label: 'live', tone: 'live' },
      error: eq.error,
    };
  } else {
    const d = eq.data;
    if (!d) {
      eqProps = {
        state: 'error',
        source: 'USGS Earthquake',
        freshness: { label: 'live', tone: 'live' },
        error: 'No earthquake feed',
      };
    } else if (d.count === 0) {
      eqProps = {
        state: 'ok',
        source: 'USGS Earthquake',
        freshness: { label: 'live', tone: 'live' },
        value: '0 events',
        labelLine: 'No M≥2.5 within 100 km in last 7 days',
        sub: 'earthquake.usgs.gov · all_week feed',
      };
    } else {
      const top = d.events[0];
      eqProps = {
        state: 'ok',
        source: 'USGS Earthquake',
        freshness: { label: 'live', tone: 'live' },
        value: `${d.count} events`,
        labelLine: `Largest M${top.mag} · ${top.place} · ${top.distance_km} km away`,
        sub: 'earthquake.usgs.gov · all_week feed',
      };
    }
  }

  return (
    <>
      <DataCard
        source="EPA RCRA · ECHO"
        freshness={
          rcra.state === 'ok' && rcra.freshness ? rcra.freshness : { label: 'daily', tone: 'live' }
        }
        {...rcra}
      />
      <DataCard {...triProps} />
      <DataCard {...eqProps} />
    </>
  );
};

// ─── discharge intelligence card ─────────────────────────────────────────────

const DischargeIntelCard: React.FC = () => {
  const { dischargeData: data, dischargeLoading: loading, dischargeError: error } =
    useFacilityModeData();
  if (loading) {
    return (
      <DataCard
        source="EPA ECHO DMR"
        freshness={{ label: 'monthly', tone: 'live' }}
        state="loading"
      />
    );
  }
  if (!data || data.length === 0) {
    const message = error?.includes('HTTP 429')
      ? 'Data unavailable — ECHO rate limit reached, retrying later'
      : 'Data unavailable — please try again';
    return (
      <DataCard
        source="EPA ECHO DMR"
        freshness={{ label: 'monthly', tone: 'live' }}
        state="error"
        error={message}
      />
    );
  }

  const totalExceedances = data.reduce((s, f) => s + f.summary.totalExceedances12mo, 0);
  const totalOutfalls = data.reduce((s, f) => s + f.summary.totalOutfalls, 0);
  const totalParamRows = data.reduce((s, f) => s + f.summary.totalParameterRows, 0);

  const paramExceedMap = new Map<string, number>();
  data.forEach((f) =>
    f.outfalls.forEach((o) =>
      o.parameters.forEach((p) => {
        if (p.exceedanceCount12mo > 0) {
          paramExceedMap.set(
            p.parameterName,
            (paramExceedMap.get(p.parameterName) ?? 0) + p.exceedanceCount12mo
          );
        }
      })
    )
  );
  let mostExceededParam: string | null = null;
  let maxCount = 0;
  paramExceedMap.forEach((count, name) => {
    if (count > maxCount) {
      maxCount = count;
      mostExceededParam = name;
    }
  });

  const topOutfall = data
    .flatMap((f) => f.outfalls)
    .reduce<{ id: string; count: number } | null>(
      (best, o) =>
        !best || o.exceedanceCount12mo > best.count
          ? { id: o.id, count: o.exceedanceCount12mo }
          : best,
      null
    );

  const lastExceedanceDate = data.reduce<string | null>((latest, f) => {
    return f.summary.lastExceedanceDate ?? latest;
  }, null);

  const mostRecentDMRPeriod =
    data.find((f) => f.summary.mostRecentDMRPeriod)?.summary.mostRecentDMRPeriod ?? null;
  const facilityCount = data.length;

  const valueStr = `${totalExceedances} exceedance${totalExceedances !== 1 ? 's' : ''} · ${totalOutfalls} outfall${totalOutfalls !== 1 ? 's' : ''}${facilityCount > 1 ? ` across ${facilityCount} facilities` : ''}`;

  return (
    <DataCard
      source="EPA ECHO DMR"
      freshness={{ label: mostRecentDMRPeriod ?? 'monthly', tone: 'live' }}
      state="ok"
      value={valueStr}
      labelLine={
        [
          topOutfall && topOutfall.count > 0 ? `Most exceeded outfall: ${topOutfall.id}` : null,
          mostExceededParam ? `Most exceeded param: ${mostExceededParam}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || `${totalParamRows} parameter-limit rows monitored`
      }
      sub={`Last exceedance: ${lastExceedanceDate ?? 'none'} · echo.epa.gov`}
    />
  );
};

// ─── main overlay component ───────────────────────────────────────────────────

const FacilityModeOverlay: React.FC = () => {
  const {
    activeMarkerRecord,
    currentMode,
    setCurrentMode,
    airnow,
    nws,
    echo,
    usgs,
    nwps,
    eq,
    selectedOutfall,
    setSelectedOutfall,
    attainsPanelOpen,
  } = useFacilityModeData();

  if (!activeMarkerRecord) return null;

  return (
    <Overlay>
      <ModeToggleBar>
        <ModeToggleGroup role="tablist" aria-label="Data mode">
          {MODES.map((m) => (
            <ModePill
              key={m.id}
              role="tab"
              aria-selected={currentMode === m.id}
              $active={currentMode === m.id}
              $color={m.color}
              onClick={() => setCurrentMode(m.id)}
            >
              <span aria-hidden="true">{m.icon}</span> {m.label}
            </ModePill>
          ))}
        </ModeToggleGroup>
      </ModeToggleBar>

      {selectedOutfall && currentMode === 'water' && (
        <OutfallDetailPanel
          key={selectedOutfall.id}
          outfall={selectedOutfall}
          onClose={() => setSelectedOutfall(null)}
        />
      )}

      {attainsPanelOpen && currentMode === 'water' && <ReceivingWaterDetailPanel />}

      <CardsArea>
        {currentMode === 'air' && (
          <>
            <AirCards airnow={airnow} nws={nws} />
            <EchoAirCard echo={echo} />
          </>
        )}
        {currentMode === 'water' && (
          <>
            <WaterCards usgs={usgs} echo={echo} nwps={nwps} />
            <ReceivingWaterStatusCard />
            <DischargeIntelCard />
          </>
        )}
        {currentMode === 'land' && <LandCards echo={echo} eq={eq} />}
      </CardsArea>
    </Overlay>
  );
};

export default FacilityModeOverlay;
